<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  blockOwnerButtonTitle,
  canBlockOwnerPhone,
  canOpenUrlColumn,
  cellValue,
  isMidasActionVisible,
  isOwnerPhoneColumn,
  isOwnerUnconfirmedField,
  isRefusalActionVisible,
  isRegisterActionVisible,
  isSourceUrlColumn,
  midasButtonTitle,
  ownerConfirmationTooltip,
  ownerFieldTitle,
  ownerFieldValueClass,
  refusalButtonTitle,
  statusTone,
} from '../utils/pendentesFormatters';
import {
  hasSourceLinks,
  hiddenSourceLinksCount,
  visibleSourceLinks,
} from '../utils/pendentesSourceLinks';
import { usePendentesRowActions } from '../composables/usePendentesRowActions';

const props = defineProps({
  items: { type: Array, default: () => [] },
  columns: { type: Array, default: () => [] },
  isLoadingItems: { type: Boolean, default: false },
  showRowActions: { type: Boolean, default: false },
  sortBy: { type: String, default: '' },
  sortDirection: { type: String, default: '' },
  updatingMidasItemId: { type: String, default: '' },
  registeringLaudoItemId: { type: String, default: '' },
  savingRefusalItemId: { type: String, default: '' },
});

const emit = defineEmits([
  'sort',
  'midas',
  'register',
  'refuse',
  'blockOwnerPhone',
  'openSourceLinks',
]);

const {
  isLaudoRegisteringItem,
  registerButtonTitle,
  isMidasButtonDisabled,
  isRegisterButtonDisabled,
  isRefusalButtonDisabled,
} = usePendentesRowActions({
  updatingMidasItemId: computed(() => props.updatingMidasItemId),
  registeringLaudoItemId: computed(() => props.registeringLaudoItemId),
  savingRefusalItemId: computed(() => props.savingRefusalItemId),
});

const tableColumnCount = computed(
  () => props.columns.length + (props.showRowActions ? 1 : 0)
);
const tableMinWidth = computed(
  () => `${Math.max(tableColumnCount.value * 144, 960)}px`
);

function sortIcon(column) {
  if (props.sortBy !== column.sortBy) return 'i-lucide-arrow-up-down';
  return props.sortDirection === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
}
</script>

<template>
  <div class="mt-4 hidden min-w-0 lg:block">
    <LaisCard density="flush" class="min-w-0">
      <div
        class="w-full max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain"
        :aria-busy="isLoadingItems"
      >
        <table
          class="min-w-full divide-y divide-lais-border-soft text-left text-[13px]"
          :style="{ minWidth: tableMinWidth }"
        >
          <thead class="bg-[#fbfaf8] shadow-lais-soft">
            <tr>
              <th
                v-for="(column, index) in columns"
                :key="column.key"
                class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                :class="index === 0 ? 'bg-[#fbfaf8]' : ''"
              >
                <button
                  v-if="column.sortBy"
                  type="button"
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 transition hover:bg-white hover:text-lais-primary"
                  @click="emit('sort', column)"
                >
                  {{ column.label }}
                  <span
                    class="size-3.5"
                    :class="sortIcon(column)"
                    aria-hidden="true"
                  />
                </button>
                <span v-else class="px-2 py-1">{{ column.label }}</span>
              </th>
              <th
                v-if="showRowActions"
                class="whitespace-nowrap bg-[#fbfaf8] px-4 py-3 text-center text-[11px] font-bold uppercase text-lais-text-muted"
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-lais-border-soft bg-white">
            <tr v-if="isLoadingItems">
              <td
                :colspan="Math.max(tableColumnCount, 1)"
                class="px-4 py-14 text-center text-lais-text-muted"
              >
                <span
                  class="i-lucide-loader-circle mx-auto mb-2 block size-6 animate-spin text-lais-primary"
                  aria-hidden="true"
                />
                Carregando pendentes...
              </td>
            </tr>
            <tr v-else-if="!items.length">
              <td
                :colspan="Math.max(tableColumnCount, 1)"
                class="px-4 py-14 text-center text-lais-text-muted"
              >
                Nenhum imovel encontrado com os filtros atuais.
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="item in items"
                :key="item.id"
                class="group hover:bg-lais-primary-50/20"
              >
                <td
                  v-for="(column, index) in columns"
                  :key="column.key"
                  class="max-w-[260px] whitespace-nowrap px-4 py-3 align-top text-lais-text"
                  :class="
                    index === 0
                      ? 'bg-white font-semibold group-hover:bg-lais-primary-50/20'
                      : ''
                  "
                >
                  <div
                    v-if="isSourceUrlColumn(column) && hasSourceLinks(item)"
                    class="flex max-w-[260px] flex-wrap items-center gap-1.5"
                  >
                    <template
                      v-for="link in visibleSourceLinks(item)"
                      :key="link.key"
                    >
                      <span
                        v-if="link.inactive"
                        v-tooltip.top="link.title"
                        :title="link.title"
                        class="inline-flex"
                      >
                        <button
                          type="button"
                          disabled
                          class="inline-flex cursor-not-allowed items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[12px] font-semibold text-red-700 ring-1 ring-red-200"
                          :aria-label="link.title"
                        >
                          Abrir
                          <span
                            class="i-lucide-circle-off size-3"
                            aria-hidden="true"
                          />
                        </button>
                      </span>
                      <a
                        v-else
                        :href="link.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 rounded-full bg-lais-primary-50 px-2.5 py-1 text-[12px] font-semibold text-lais-primary hover:bg-lais-primary hover:text-white"
                        :title="link.title"
                      >
                        Abrir
                        <span
                          class="i-lucide-external-link size-3"
                          aria-hidden="true"
                        />
                      </a>
                    </template>
                    <button
                      v-if="hiddenSourceLinksCount(item)"
                      type="button"
                      class="inline-flex h-7 items-center rounded-full border border-lais-border bg-white px-2 text-[12px] font-semibold text-lais-text-muted transition hover:border-lais-primary hover:bg-lais-primary-50 hover:text-lais-primary"
                      :aria-label="`Mostrar mais ${hiddenSourceLinksCount(item)} anúncio(s)`"
                      @click.stop="emit('openSourceLinks', item)"
                    >
                      +{{ hiddenSourceLinksCount(item) }}
                    </button>
                  </div>
                  <a
                    v-else-if="canOpenUrlColumn(item, column)"
                    :href="item[column.key]"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 rounded-full bg-lais-primary-50 px-2.5 py-1 text-[12px] font-semibold text-lais-primary hover:bg-lais-primary hover:text-white"
                  >
                    Abrir
                    <span
                      class="i-lucide-external-link size-3"
                      aria-hidden="true"
                    />
                  </a>
                  <span
                    v-else-if="column.type === 'status'"
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                    :class="statusTone(item.status)"
                  >
                    {{ item.statusLabel || cellValue(item, column) }}
                  </span>
                  <span
                    v-else
                    v-tooltip.top="
                      isOwnerUnconfirmedField(item, column)
                        ? ownerConfirmationTooltip(item)
                        : null
                    "
                    class="truncate"
                    :class="[
                      isOwnerPhoneColumn(column) && canBlockOwnerPhone(item)
                        ? 'inline-flex max-w-[240px] items-center gap-1.5'
                        : 'block',
                      ownerFieldValueClass(item, column),
                    ]"
                    :title="ownerFieldTitle(item, column)"
                  >
                    <span class="truncate">{{ cellValue(item, column) }}</span>
                    <button
                      v-if="
                        isOwnerPhoneColumn(column) && canBlockOwnerPhone(item)
                      "
                      v-tooltip.top="blockOwnerButtonTitle(item)"
                      type="button"
                      class="inline-grid size-6 shrink-0 place-items-center rounded-full border border-red-100 bg-white text-red-700 transition hover:border-red-500 hover:bg-red-600 hover:text-white"
                      :title="blockOwnerButtonTitle(item)"
                      :aria-label="blockOwnerButtonTitle(item)"
                      @click.stop="emit('blockOwnerPhone', item)"
                    >
                      <span class="i-lucide-ban size-3" aria-hidden="true" />
                    </button>
                  </span>
                </td>
                <td
                  v-if="showRowActions"
                  class="bg-white px-4 py-3 text-center group-hover:bg-lais-primary-50/20"
                >
                  <div class="flex justify-center gap-2">
                    <button
                      v-if="isMidasActionVisible(item)"
                      v-tooltip.top="midasButtonTitle(item)"
                      type="button"
                      class="inline-grid size-9 place-items-center rounded-full border border-lais-border bg-white text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary hover:text-white disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-lais-bg-page disabled:text-lais-text-muted"
                      :title="midasButtonTitle(item)"
                      :aria-label="midasButtonTitle(item)"
                      :disabled="isMidasButtonDisabled(item)"
                      @click="emit('midas', item)"
                    >
                      <span
                        class="size-4"
                        :class="
                          updatingMidasItemId === String(item.id)
                            ? 'i-lucide-loader-circle animate-spin'
                            : 'i-lucide-refresh-cw'
                        "
                        aria-hidden="true"
                      />
                      <span class="sr-only">Atualizar no Midas</span>
                    </button>
                    <button
                      v-if="isRegisterActionVisible(item)"
                      v-tooltip.top="registerButtonTitle(item)"
                      type="button"
                      class="inline-grid size-9 place-items-center rounded-full border border-woot-100 bg-woot-50 text-woot-700 transition hover:border-woot-600 hover:bg-woot-600 hover:text-white disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-lais-bg-page disabled:text-lais-text-muted"
                      :title="registerButtonTitle(item)"
                      :aria-label="registerButtonTitle(item)"
                      :disabled="isRegisterButtonDisabled(item)"
                      @click="emit('register', item)"
                    >
                      <span
                        class="size-4"
                        :class="
                          isLaudoRegisteringItem(item)
                            ? 'i-lucide-loader-circle animate-spin'
                            : 'i-lucide-file-plus-2'
                        "
                        aria-hidden="true"
                      />
                      <span class="sr-only"> Cadastrar laudo no Midas </span>
                    </button>
                    <button
                      v-if="isRefusalActionVisible(item)"
                      v-tooltip.top="refusalButtonTitle(item)"
                      type="button"
                      class="inline-grid size-9 place-items-center rounded-full border border-red-100 bg-red-50 text-red-700 transition hover:border-red-600 hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-lais-bg-page disabled:text-lais-text-muted"
                      :title="refusalButtonTitle(item)"
                      :aria-label="refusalButtonTitle(item)"
                      :disabled="isRefusalButtonDisabled(item)"
                      @click="emit('refuse', item)"
                    >
                      <span
                        class="size-4"
                        :class="
                          savingRefusalItemId === String(item.id)
                            ? 'i-lucide-loader-circle animate-spin'
                            : 'i-lucide-ban'
                        "
                        aria-hidden="true"
                      />
                      <span class="sr-only"> Registrar negativa </span>
                    </button>
                    <span
                      v-if="
                        !isMidasActionVisible(item) &&
                        !isRegisterActionVisible(item) &&
                        !isRefusalActionVisible(item)
                      "
                      class="text-lais-text-muted"
                    >
                      -
                    </span>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </LaisCard>
  </div>
</template>
